// Following an invitation.
//
// ---------------------------------------------------------------------------
// THE TOKEN IS IN THE FRAGMENT, AND THIS SCREEN'S JOB IS TO KEEP IT THERE
// ---------------------------------------------------------------------------
//
// `packages/auth/src/token.ts` mails `https://app.example/invite#<token>` and says why: a
// fragment is never transmitted, so the token is absent from the origin's access log, from
// every proxy in front of it, and from the `Referer` header of everything this page then
// loads. `POST /v1/invitations/redeem` takes the token in the BODY for the same reason —
// there is deliberately no `GET /v1/invitations/:token/redeem`.
//
// The client's half of that: read `location.hash`, and clear it with `replaceState` before
// anything else happens. Left in place, the token sits in the address bar of a browser on
// a shared kitchen table and in that browser's history, and redemption binds a roster entry
// permanently — whoever redeems it is the person the family talks to from then on.
//
// The token is never put in a query string, never logged, and never rendered.
//
// ---------------------------------------------------------------------------
// THE PERSON FOLLOWING THE LINK IS USUALLY NOT SIGNED IN, AND USUALLY NEW
// ---------------------------------------------------------------------------
//
// A mail link opens a fresh tab, where there is no session yet — and with the vendor
// configured there is no session for a few seconds EVEN FOR A SIGNED-IN PERSON, because
// the vendor's scripts load and mint the first token after this screen has mounted. The
// first version of this screen checked the session once, at mount, and told everyone to
// "sign in first, then follow the link again" — which reads as broken to the person who
// just did sign in, and was: the check ran before the session could possibly exist.
//
// So the token moves from the fragment into `sessionStorage` — the same per-tab, dies-
// with-the-tab store the session itself uses — and this screen simply waits for a session
// to appear, from wherever it appears: the vendor hydrating an existing sign-in, or the
// person signing up right here. Signed out, it mounts the vendor's SIGN-UP component
// inline (most invitees are new people; the component carries its own sign-in link for
// the rest), pinned back to this route so a post-sign-up redirect lands where the stash
// is. The moment a session token exists, redemption fires — one click on the mail link,
// end to end, with nothing to come back and do again.
//
// ---------------------------------------------------------------------------
// THE ADDRESS ARRIVES LATE, AND THE VENDOR CANNOT BE REMOUNTED FOR IT
// ---------------------------------------------------------------------------
//
// `VendorSignUp` mounts the vendor's component in an effect with an empty dependency list,
// deliberately: it mounts once and is torn down on unmount, and nothing about a person's
// half-typed sign-up survives a remount. That means an address that shows up AFTER the
// mount can never reach it - adding it to the dependency list would remount the component
// the instant a fetch resolved, discarding whatever the person had already started typing.
//
// So the address is fetched in `Invite` itself, before `VendorSignUp` is rendered at all,
// and `VendorSignUp` is held back until that fetch has settled - success or refusal, it
// always settles, it never hangs. There is no spinner and no second line of copy for the
// wait: the lede already on screen is true the whole time, and the wait is short - the
// vendor's own scripts are loading over the same seconds this fetch is in flight. A failed
// or refused lookup is not a problem the person ever sees: sign-up mounts with an empty
// field, exactly as it does today with no prefill at all.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Clerk } from '@clerk/clerk-js';

import { ApiFailure, type ApiClient } from '../api/client.js';
import { clerkConfigured, getClerk } from '../clerk.js';
import { pendingInviteToken, stashInviteToken } from '../invite-stash.js';
import { session } from '../session.js';
import { vendorAppearance } from '../vendor-appearance.js';

export type InviteProps = {
  client: ApiClient;
};

export type VendorSignUpProps = {
  /**
   * The address to start the form with, or `''` for no prefill — a failed or refused
   * lookup, or a build with no server route to ask at all. Read once, at mount: see the
   * file header for why this component's own effect cannot pick up a value that changes
   * after that.
   */
  email: string;
};

/**
 * The vendor's sign-up, mounted inline so the invitation flow never leaves this route.
 * Redirects are pinned back here: the stash is what survives them.
 */
function VendorSignUp({ email }: VendorSignUpProps): JSX.Element {
  const mount = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = mount.current;
    const vendor = getClerk();
    if (element === null || vendor === null) {
      return undefined;
    }
    let unmounted = false;
    const props = {
      forceRedirectUrl: '/invite',
      signInForceRedirectUrl: '/invite',
      appearance: vendorAppearance(),
      // `email` is a prop, not a dependency: this effect's empty array below means it
      // reads whatever value `VendorSignUp` was mounted with and never again, which is
      // exactly what the caller already promises by holding this component back until
      // the address lookup has settled before rendering it at all.
      ...(email === '' ? {} : { initialValues: { emailAddress: email } }),
    } as Parameters<Clerk['mountSignUp']>[1];
    void vendor
      .then((clerk) => {
        if (!unmounted) {
          clerk.mountSignUp(element, props);
        }
      })
      .catch(() => {
        if (!unmounted) {
          setFailed(true);
        }
      });
    return () => {
      unmounted = true;
      void vendor.then((clerk) => clerk.unmountSignUp(element)).catch(() => undefined);
    };
  }, []);

  return (
    <>
      <div ref={mount} />
      {failed ? (
        <p className="note" role="status">
          Sign-up didn’t load. Check the connection and reload this page.
        </p>
      ) : null}
    </>
  );
}

/**
 * The vendorless fallback: the pasted-token form the suites and a keyless dev loop use.
 * Unlike the sign-in screen's, it does not navigate — establishing the session is enough,
 * and the redemption below fires on it.
 */
function PastedToken(): JSX.Element {
  const [value, setValue] = useState('');
  return (
    <form
      className="signin"
      onSubmit={(event) => {
        event.preventDefault();
        if (value.trim() === '') {
          return;
        }
        session.signIn(value);
      }}
    >
      <label className="signin__label" htmlFor="signin-token">
        Sign-in token
      </label>
      <input
        id="signin-token"
        className="signin__field"
        value={value}
        autoComplete="off"
        onChange={(event) => setValue(event.target.value)}
      />
      <button
        type="submit"
        className="button button--primary"
        disabled={value.trim() === ''}
      >
        Continue
      </button>
    </form>
  );
}

export function Invite({ client }: InviteProps): JSX.Element {
  const [problem, setProblem] = useState<string | null>(null);
  const [alreadyInPlan, setAlreadyInPlan] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [retrySignal, setRetrySignal] = useState(0);
  const navigate = useNavigate();
  const attempted = useRef(false);
  const addressRequested = useRef(false);
  // `ready` false is "still waiting"; it is never used to mean "no address" — a settled
  // lookup with nothing to prefill still sets it true, alongside an empty `email`. `isDemo`
  // is meaningless until `ready` is true, the same way `email` is.
  const [prefill, setPrefill] = useState<{
    ready: boolean;
    email: string;
    isDemo: boolean;
  }>({
    ready: false,
    email: '',
    isDemo: false,
  });

  // The fragment is consumed exactly once, before the first paint, and survives in the
  // stash rather than in this component's lifetime: a sign-up that reloads the page
  // arrives back here with no fragment and finds the token waiting.
  const [invite] = useState<string | null>(() => {
    const fromHash = window.location.hash.replace(/^#/u, '');
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
    if (fromHash !== '') {
      stashInviteToken(fromHash);
      return fromHash;
    }
    return pendingInviteToken();
  });

  const token = useSyncExternalStore(
    session.subscribe,
    () => session.token(),
    () => null,
  );

  const retry = useCallback((): void => {
    attempted.current = false;
    setRetryable(false);
    setRetrySignal((value) => value + 1);
  }, []);

  // A browser coming back online is an actual retry trigger. Merely preserving the
  // invitation is not enough: the bearer may remain unchanged for another minute, and
  // connectivity recovery should not depend on an unrelated Clerk refresh.
  useEffect(() => {
    // Only listen after an attempt has failed. An `online` event during an in-flight
    // request must not start a second redemption with the same invitation.
    if (!retryable) {
      return undefined;
    }
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [retry, retryable]);

  useEffect(() => {
    if (invite === null || token === null || attempted.current) {
      return;
    }
    attempted.current = true;
    setRetryable(false);
    client.redeemInvitation(invite).then(
      (planId) => {
        stashInviteToken(null);
        navigate(`/plans/${planId}`, { replace: true });
      },
      (error: unknown) => {
        // The one refusal the server answers distinctly: the caller already holds a grant
        // on the plan this token names. Terminal in the same sense as the 404 below — the
        // server looked at the token and there is nothing to retry — but it is not a
        // failure, so it gets its own screen and a way in rather than "ask for a new one".
        // A server older than this change carries no plan id, and a malformed one is
        // dropped by `failureFrom` before it ever reaches here; either way, fall back to the
        // uniform copy rather than rendering a button that goes nowhere.
        if (
          error instanceof ApiFailure &&
          error.status === 409 &&
          error.code === 'already_in_plan'
        ) {
          stashInviteToken(null);
          if (typeof error.planId === 'string') {
            setAlreadyInPlan(error.planId);
          } else {
            setProblem('That invitation didn’t work. Ask for a new one.');
          }
          return;
        }
        // Terminal only when the server LOOKED at the token and refused it: the
        // deliberately uniform 404, or the 400 that says the request itself is
        // malformed. NAMED, not derived — "everything under 500 except 401" quietly
        // included the client's status-0 network failure and made going through a
        // tunnel delete the invitation. Everything unnamed is a failure of the attempt,
        // not of the invitation — a stale bearer (401), a rate limit (429), a server
        // error, no network — and keeps the stash with the attempt cleared; the next
        // bearer value the vendor publishes reruns this effect.
        const terminal =
          error instanceof ApiFailure && (error.status === 404 || error.status === 400);
        if (terminal) {
          stashInviteToken(null);
          setProblem('That invitation didn’t work. Ask for a new one.');
          return;
        }
        attempted.current = false;
        // A 401 waits silently for Clerk to publish a different bearer, which is already
        // a dependency of this effect. Transport, rate-limit, server and unexpected
        // failures may leave the bearer unchanged, so give them both the browser's
        // `online` retry above and a deliberate retry control below.
        if (!(error instanceof ApiFailure && error.status === 401)) {
          setRetryable(true);
        }
      },
    );
  }, [client, navigate, invite, token, retrySignal]);

  // Asked once, only on the path that can use it: a session already redeems above without
  // ever reaching this effect, and the pasted-token fallback below never reads `prefill` at
  // all, so there is nothing to gain by asking for it there.
  useEffect(() => {
    if (
      invite === null ||
      token !== null ||
      addressRequested.current ||
      !clerkConfigured()
    ) {
      return;
    }
    addressRequested.current = true;
    client.invitationAddress(invite).then(
      (address) =>
        setPrefill({ ready: true, email: address.email, isDemo: address.isDemo }),
      () => {
        // A failed or refused lookup is not an error the person sees — see the file
        // header. Settling with no address is `VendorSignUp` mounting exactly as it
        // does today, with nothing pre-filled.
        setPrefill({ ready: true, email: '', isDemo: false });
      },
    );
  }, [client, invite, token]);

  if (invite === null) {
    return (
      <main className="screen screen--invite">
        <h1 className="screen__title">
          That invitation link is incomplete. Ask for a new one.
        </h1>
      </main>
    );
  }

  if (problem !== null) {
    return (
      <main className="screen screen--invite">
        <h1 className="screen__title">{problem}</h1>
      </main>
    );
  }

  if (alreadyInPlan !== null) {
    return (
      <main className="screen screen--invite">
        <h1 className="screen__title">You’re already in this plan.</h1>
        <p className="note">
          This invitation is for someone else. Nothing is wrong with it.
        </p>
        <button
          type="button"
          className="button button--primary"
          onClick={() => navigate(`/plans/${alreadyInPlan}`)}
        >
          Open the plan
        </button>
      </main>
    );
  }

  if (token === null) {
    return (
      <main className="screen screen--invite">
        <h1 className="screen__title">You’ve been invited to a conversation on Mor</h1>
        {prefill.ready && prefill.isDemo ? (
          <p className="note">
            This is a demonstration. The family in it is invented, and the people you’re
            about to meet are played by a model.
          </p>
        ) : null}
        {clerkConfigured() ? (
          prefill.ready ? (
            <VendorSignUp email={prefill.email} />
          ) : null
        ) : (
          <PastedToken />
        )}
      </main>
    );
  }

  if (retryable) {
    return (
      <main className="screen screen--invite">
        <h1 className="screen__title">We couldn’t finish joining the plan.</h1>
        <button type="button" className="button button--primary" onClick={retry}>
          Try again
        </button>
      </main>
    );
  }

  return (
    <main className="screen screen--invite">
      <h1 className="screen__title">Getting you in…</h1>
    </main>
  );
}
